<template>
  <div :class="['flex flex-col h-full bg-blue-50', withMargin ? 'ml-12' : '']" style="width:100%; max-width:320px; margin:0 auto;">
    <!-- 검색창 (view all에서만) -->
    <div v-if="showSearch" class="px-4 pt-4">
      <input
        v-model="searchQuery"
        type="text"
        placeholder="채팅 기록 검색 ..."
        class="w-full px-4 py-2 border-2 border-gray-300 rounded-lg focus:outline-none focus:shadow-lg focus:border-gray-300 transition-shadow duration-200"
        title="채팅 기록 검색"
      />
    </div>
    <!-- 전체 선택/개수/삭제 버튼 (view all에서만) -->
    <div v-if="showCheckbox" class="flex items-center justify-between px-4 py-2">
      <div class="flex items-center gap-4 mt-2">
        <input
          type="checkbox"
          :checked="isAllSelected"
          @change="toggleSelectAll"
          class="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
          title="전체 선택"
        />
        <span title="전체 선택">전체 선택</span>
        <span class="text-sm text-gray-600 ml-2">총 {{ filteredChats.length }}개</span>
      </div>
      <button
        v-if="showDelete && selectedChats.length > 0"
        @click="deleteSelected"
        class="px-3 py-1 text-sm rounded-lg bg-red-500 text-white hover:bg-red-600"
        title="선택한 채팅 삭제"
      >
        삭제
      </button>
    </div>

    <!-- 채팅 목록 -->
    <div class="flex-1 overflow-y-auto" style="max-height:100%;">
      <div v-if="isLoading" class="flex items-center justify-center h-full">
        <span class="loader spinner-border animate-spin inline-block w-8 h-8 border-4 rounded-full border-blue-400 border-t-transparent mt-5"></span>
      </div>
      <div v-else>
        <div v-if="filteredChats.length === 0" :class="['text-center text-gray-500 py-4', isSidebar ? 'bg-blue-50' : '']">
          대화 내역이 없습니다.
        </div>
        <div v-else>
          <div v-for="([date, chats]) in groupedChatsByDate" :key="date">
            <div class="text-xm text-black font-semibold mt-1 mb-1 pl-2 bg-blue-50 rounded py-1 w-fit">{{ date }}</div>
            <div v-for="chat in chats" :key="chat.id"
              :class="[
                'p-2 pl-1 cursor-pointer',
                'bg-blue-50 hover:bg-blue-100',
                chat.id === currentChatId ? 'bg-blue-200 rounded-lg' : 'rounded-lg'
              ]"
              @click="handleChatClick(chat.id)"
              title="채팅 보기"
            >
              <div class="flex items-center">
                <!-- 체크박스 영역 -->
                <div v-if="showCheckbox" class="mr-4" @click.stop>
                  <input
                    type="checkbox"
                    :checked="isSelected(chat.id)"
                    @click.stop="toggleSelect(chat.id)"
                    class="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    title="채팅 선택"
                  />
                </div>
                <!-- 채팅 정보 영역 -->
                <div 
                  class="flex-1"
                >
                  <div class="font-medium text-gray-700 ellipsis-title ml-2">{{ chat.title }}</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { chatAPI } from '@/utils/api';

export default {
  name: 'ChatHistory',
  props: {
    chats: {
      type: Array,
      required: true
    },
    showCheckbox: {
      type: Boolean,
      default: false
    },
    showSearch: {
      type: Boolean,
      default: false
    },
    showDelete: {
      type: Boolean,
      default: false
    },
    currentChatId: {
      type: String,
      default: null
    },
    withMargin: {
      type: Boolean,
      default: false
    },
    isSidebar: {
      type: Boolean,
      default: false
    },
    isLoading: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      searchQuery: '',
      selectedChats: []
    }
  },
  computed: {
    filteredChats() {
      if (!this.searchQuery) return this.chats
      const query = this.searchQuery.toLowerCase()
      return this.chats.filter(chat =>
        chat.title.toLowerCase().includes(query)
      )
    },
    groupedChatsByDate() {
      // 날짜별로 그룹핑 (yyyy-mm-dd)
      const groups = {};
      const dateKey = (ts) => {
        const d = new Date(ts + 0);                // ts = epoch(ms)
        const y = d.getFullYear();
        const m = String(d.getMonth() + 1).padStart(2, '0');
        const day = String(d.getDate()).padStart(2, '0');
        return `${y}-${m}-${day}`;                 // 2025-06-19
      };
      this.filteredChats.forEach(chat => {
        let ts = chat.created_at_ms;
        if (!ts && chat.created_at) {
          // created_at이 'yyyy-MM-ddTHH:mm:ssZ' 형태 등일 때 Date.parse로 파싱
          const parsed = Date.parse(chat.created_at.replace(/\./g, '-'));
          if (!isNaN(parsed)) ts = parsed;
        }
        // ts가 여전히 없거나 유효하지 않으면 현재 시각으로 보정하여 NaN- 표시 방지
        if (!ts || isNaN(ts)) ts = Date.now();
        const date = dateKey(ts);
        if (!groups[date]) groups[date] = [];
        groups[date].push(chat);
      });
      // 날짜 내림차순 정렬
      return Object.entries(groups).sort((a, b) => b[0].localeCompare(a[0]));
    },
    isAllSelected() {
      return this.filteredChats.length > 0 && this.selectedChats.length === this.filteredChats.length
    }
  },
  methods: {
    async handleChatClick(chatId) {
      try {
        if (this.showCheckbox) {
          // 체크박스 모드에서는 채팅 선택 이벤트만 발생
          this.$emit('select-chat', chatId);
        } else {
          // 일반 모드에서는 채팅 선택 이벤트 발생
          this.$emit('select-chat', chatId);
        }
      } catch (error) {
        console.error('채팅 선택 실패:', error);
      }
    },

    toggleSelect(chatId) {
      const index = this.selectedChats.indexOf(chatId);
      if (index === -1) {
        this.selectedChats.push(chatId);
      } else {
        this.selectedChats.splice(index, 1);
      }
    },

    isSelected(chatId) {
      return this.selectedChats.includes(chatId);
    },

    async deleteSelected() {
      if (this.selectedChats.length === 0) return;
      try {
        await chatAPI.deleteChats(this.selectedChats);
        this.$emit('delete-selected', this.selectedChats);
        this.selectedChats = [];
      } catch (error) {
        console.error('채팅 삭제 실패:', error);
      }
    },

    formattedDate(chat) {
      if (!chat?.created_at) return '';
      let ts = chat.created_at_ms;
      if (!ts && chat.created_at) {
        const d = new Date(chat.created_at.replace(/\\./g,'-'));
        if (!isNaN(d)) ts = d.getTime();
      }
      if (!ts) return '';
      const date = new Date(ts + 9*60*60*1000);
      return date.toISOString().slice(0,10);
    },

    toggleSelectAll() {
      if (this.isAllSelected) {
        this.selectedChats = []
      } else {
        this.selectedChats = this.filteredChats.map(chat => chat.id)
      }
    }
  }
}
</script>

<style>
input:focus {
  outline: none !important;
  box-shadow: 0 4px 6px -1px rgba(0,0,0,0.1), 0 2px 4px -1px rgba(0,0,0,0.06) !important;
  border-color: #e5e7eb !important;
}
</style>

<style scoped>
.ellipsis-title {
  max-width: 260px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  display: inline-block;
  vertical-align: middle;
}

.loader {
  border-width: 4px;
  border-style: solid;
  border-radius: 50%;
  border-color: #60a5fa #60a5fa #60a5fa transparent;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}
</style> 